/* Arrows and a progress bar for horizontal card scrollers.
 *
 * The scrolling itself is CSS — overflow-x plus scroll snapping — so a section
 * using this still swipes by touch and trackpad if the file never loads.
 * Everything here is additive: it steps the scroller one card per arrow press
 * and keeps the bar in step with wherever the cards have been dragged to.
 *
 * Markup contract, all on or inside [data-nr-carousel]:
 *   [data-nr-carousel-scroller]  the element that actually scrolls
 *   [data-nr-carousel-prev]      button, optional
 *   [data-nr-carousel-next]      button, optional
 *   [data-nr-carousel-thumb]     progress indicator, optional
 */
(function () {
  'use strict';

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function setup(root) {
    var scroller = root.querySelector('[data-nr-carousel-scroller]');
    if (!scroller) return;

    var prev = root.querySelector('[data-nr-carousel-prev]');
    var next = root.querySelector('[data-nr-carousel-next]');
    var thumb = root.querySelector('[data-nr-carousel-thumb]');
    var frame = null;

    // One card plus one gap. Measured rather than assumed, because the card
    // width is a percentage that changes at every breakpoint.
    function step() {
      var card = scroller.firstElementChild;
      if (!card) return scroller.clientWidth;
      var gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    function update() {
      frame = null;

      var max = scroller.scrollWidth - scroller.clientWidth;

      if (max > 1) {
        root.removeAttribute('data-nr-carousel-static');
      } else {
        root.setAttribute('data-nr-carousel-static', '');
      }

      // A pixel of slack at each end: fractional card widths mean scrollLeft
      // rarely lands exactly on 0 or on max, which would otherwise leave an
      // arrow live with nowhere left to go.
      if (prev) prev.disabled = scroller.scrollLeft <= 1;
      if (next) next.disabled = scroller.scrollLeft >= max - 1;

      if (thumb) {
        var visible = scroller.scrollWidth ? scroller.clientWidth / scroller.scrollWidth : 1;
        var progress = max > 0 ? scroller.scrollLeft / max : 0;
        if (visible > 1) visible = 1;
        thumb.style.width = (visible * 100) + '%';
        thumb.style.left = (progress * (100 - visible * 100)) + '%';
      }
    }

    function schedule() {
      if (frame === null) frame = requestAnimationFrame(update);
    }

    function go(direction) {
      scroller.scrollBy({
        left: direction * step(),
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });

    scroller.addEventListener('scroll', schedule, { passive: true });

    // Card widths are percentages, so a resize changes both the step size and
    // how much of the track is on screen.
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(schedule).observe(scroller);
    } else {
      window.addEventListener('resize', schedule);
    }

    // Card images are lazy loaded and change scrollWidth as they arrive.
    window.addEventListener('load', schedule);

    update();
  }

  function start() {
    var roots = document.querySelectorAll('[data-nr-carousel]');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // The theme editor rebuilds a section's markup in place, which throws away the
  // listeners bound above.
  document.addEventListener('shopify:section:load', function (event) {
    var roots = event.target.querySelectorAll('[data-nr-carousel]');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
  });
})();
